import { useEffect, useState } from "react";
import { useZone } from "../context/ZoneContext";
import { Newspaper, MapPin, ExternalLink, ChevronDown } from "lucide-react";

const CM_CITIES = [
  { name: "Douala", lat: 4.05, lng: 9.70 },
  { name: "Yaoundé", lat: 3.87, lng: 11.52 },
  { name: "Bafoussam", lat: 5.48, lng: 10.42 },
  { name: "Bamenda", lat: 5.96, lng: 10.15 },
  { name: "Bertoua", lat: 4.58, lng: 13.68 },
  { name: "Ngaoundéré", lat: 7.32, lng: 13.58 },
  { name: "Garoua", lat: 9.30, lng: 13.40 },
  { name: "Maroua", lat: 10.59, lng: 14.32 },
];

const EU_CITIES = [
  { name: "Bruxelles", lat: 50.85, lng: 4.35 },
  { name: "Anvers", lat: 51.22, lng: 4.40 },
  { name: "Gand", lat: 51.05, lng: 3.72 },
  { name: "Liège", lat: 50.63, lng: 5.58 },
  { name: "Charleroi", lat: 50.41, lng: 4.44 },
  { name: "Paris", lat: 48.86, lng: 2.35 },
  { name: "Amsterdam", lat: 52.37, lng: 4.90 },
];

const nearestCity = (list, lat, lng) => {
  let best = list[0], dmin = Infinity;
  for (const c of list) {
    const d = (c.lat - lat) ** 2 + (c.lng - lng) ** 2;
    if (d < dmin) { dmin = d; best = c; }
  }
  return best.name;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ActusVille() {
  const { zone } = useZone(); // "africa" | "europe" (IP + override super admin)
  const [city, setCity] = useState("Douala");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isEu = zone === "europe";
  const cities = isEu ? EU_CITIES : CM_CITIES;
  const regionLabel = isEu ? "Europe" : "Cameroun";

  // Zone fournie par ZoneContext (détection IP autoritaire + override super admin)

  // Quand la zone est connue : ville par défaut + affinage par GPS dans la bonne liste
  useEffect(() => {
    if (!zone) return;
    const list = zone === "europe" ? EU_CITIES : CM_CITIES;
    setCity(list[0].name);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCity(nearestCity(list, pos.coords.latitude, pos.coords.longitude)),
        () => {},
        { timeout: 5000 }
      );
    }
  }, [zone]);

  useEffect(() => {
    if (!zone) return;
    const region = zone === "europe" ? "europe" : "africa";
    const load = (spin) => {
      if (spin) setLoading(true);
      fetch(`${BACKEND}/api/city-news?city=${encodeURIComponent(city)}&region=${region}`)
        .then((r) => r.json())
        .then((j) => setItems(j.items || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    load(true);
    const id = setInterval(() => load(false), 300000);
    return () => clearInterval(id);
  }, [city, zone]);

  return (
    <div data-testid="actus-ville" data-zone={zone} className="rounded-3xl bg-white border border-[#E2DFD8] shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-4 text-[#1A1A1A]">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-xs font-black uppercase tracking-wide text-[#F26419] flex items-center gap-1.5"><Newspaper className="w-4 h-4" /> Actus par ville</span>
        <div className="relative">
          <select
            data-testid="actus-ville-select"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="appearance-none text-xs font-bold text-[#1A1A1A] bg-[#FDF8F0] border border-[#E2DFD8] rounded-full pl-3 pr-7 py-1.5 focus:outline-none focus:border-[#F26419] cursor-pointer"
          >
            {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-[#75807A] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-[#75807A] mb-2"><MapPin className="w-3.5 h-3.5 text-[#F26419]" /> Actualités de {city} · {regionLabel}</div>

      {loading && <div className="py-4 text-center text-sm text-[#75807A]">Chargement des actus…</div>}
      {!loading && items.length === 0 && <div className="py-4 text-center text-sm text-[#75807A]">Aucune actualité pour l'instant.</div>}
      <div className="space-y-2">
        {items.slice(0, 5).map((n, i) => (
          <a
            key={i}
            data-testid={`actus-ville-item-${i}`}
            href={n.link}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-2 text-sm text-[#3B423E] hover:text-[#F26419] transition-colors group"
          >
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F26419] shrink-0" />
            <span className="flex-1 leading-snug line-clamp-2">{n.title}</span>
            <ExternalLink className="w-3.5 h-3.5 text-[#B7B2A8] group-hover:text-[#F26419] mt-0.5 shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}
